import { Fragment, useContext, useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { Dialog, Transition } from '@headlessui/react';
import { FiTrash } from "react-icons/fi";
import { EntityContext } from '../../context/EntityContext';
import { UserContext } from '../../context/UserContext';
import { APICreatePlan} from "../../api/plans/Plans";
import { APIGetCompanyAccess } from '../../api/companies/Companies';
import { SelectInput } from '../formFields/selectInput';
import { getFormatedFileSize } from '../../helper/fileSizeFormatter';
import { PlanFileUploader } from '../../pages/plans/components/files/planFileUploader';
import { FileIcon } from '../FileIcon';
import { S3DeleteAllFilesInDirectory, S3DeleteFile } from '../../helper/s3ClientHandler';
import {Analytics} from "#src/helper/analyticsHandler";
import {TfiClose} from "react-icons/tfi";

export const AddPlanAndFiles = () => {
  const cancelButtonRef = useRef(null);
  const { openPlanAndFilesModal, setPlanAndFilesModal} = useContext(EntityContext);
  const { user } = useContext(UserContext);
  const [companies, setCompanies] = useState([]);
  const [companyScope, setCompanyScope] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [planId, ] = useState(uuidv4());
  const [loading, setLoading] = useState(false);
  const [validFiles, setValidFiles] = useState(false);

  const navigate = useNavigate();

  const checkValidFiles = (files) => {
    if (!files || files.length === 0) {
      setValidFiles(false);
    } else if (files.filter(file => file.name.endsWith('.gml')).length === 0 || files.filter(file => file.name.endsWith('.gml')).length > 1) {
      setValidFiles(false);
    } else {
      setValidFiles(true);
    }
  }

  const addFilesToUploadedFiles = (files) => {
    //name is now under file.name
    const newFiles = [...uploadedFiles, ...files];
    const filteredFiles = newFiles.filter((file, index, self) =>
      index === self.findIndex((f) => f.name === file.name)
    );
    checkValidFiles(filteredFiles);
    setUploadedFiles(filteredFiles);
  }

  const removeFileFromUploadedFiles = (fileKey) => {
    const newFiles = uploadedFiles.filter((file) => file.key !== fileKey);
    checkValidFiles(newFiles);
    setUploadedFiles(newFiles);
  }

  const onFilesUploaded = async ({files}) => {
    addFilesToUploadedFiles(files);
  }

  const onPlanFinish = async () => {
    setLoading(true);
    if (uploadedFiles.length === 0) {
      return;
    }
    if (uploadedFiles.filter(file => file.name.endsWith('.gml')).length === 0) {
      alert('Es wurde keine GML Datei hochgeladen');
      return;
    }
    try {
      await APICreatePlan(planId, user, companyScope);
      Analytics("usage",`Add new Plan`, `${user?.id}`,user?.id);
      setPlanAndFilesModal(false);
      navigate(`/viewer/${planId}`);
      setLoading(false);
      return true;

    } catch (error) {
      console.error('Error creating plan:', error);
      setLoading(false);
      return false;
    }
  }

  const onPlanCancel = async () => {
    setLoading(true);
    if (uploadedFiles.length > 0) {
      await S3DeleteAllFilesInDirectory(`${companyScope}/plans/${planId}`);
    }
    setUploadedFiles([]);
    if (companies.length > 0) {
      setCompanyScope('');
    }
    setPlanAndFilesModal(false);
    setLoading(false);
  }

  const handleDeleteFile = async (fileKey) => {
    await S3DeleteFile(fileKey);
    removeFileFromUploadedFiles(fileKey);
  }

      
      
  
  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const companyAccess = await APIGetCompanyAccess(user.companyId);
        if (companyAccess === null || companyAccess.length === 0) {
          setCompanyScope(user.companyId);
        } else {
          const companiesArr = companyAccess.map((company) => {
            return {
              id: company.target_id,
              name: company.name,
            };
          });
          setCompanies(companiesArr);
          setCompanyScope('');
        }
      } catch (error) {
        console.error('Error:', error);
      }
    };
      
    fetchCompanies();
  }, [user.companyId]);

  return (
    <Transition.Root show={openPlanAndFilesModal} as={Fragment}>
      <Dialog
        className="relative z-10"
        initialFocus={cancelButtonRef}
        onClose={onPlanCancel}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex h-full items-center justify-center sm:px-4 sm:pb-4 text-center pt-[65px] sm:px-4 lg:p-10 max-w-5xl mx-auto">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className='flex flex-col max-h-full sm:max-h-max h-full rounded-lg bg-white text-left shadow-xl transition-all w-full relative'>
                <button
                    type='button'
                    className='absolute z-40 lg: lg:-top-6 lg:-right-6 top-2 right-2'
                    onClick={onPlanCancel}
                    disabled={loading}
                    ref={cancelButtonRef}
                >
                  <TfiClose className={"lg:size-16 border-gray-500/80 border-2 bg-white hover:bg-gray-300 lg:p-4 size-12 p-2 lg:rounded-full rounded-md"}/>
                </button>


                <div className='block md:flex w-full md:overflow-hidden'>
                  <div className=' rounded-tl-lg rounded-bl-lg py-8 px-8 md:flex w-full md:w-2/5 bg-gray-900 flex-col'>
                    <div>
                      <div className='flex items-center  mb-8 gap-2'>
                        <Dialog.Title
                            as='h3'
                            className='font-bold text-2xl leading-6 text-white'>
                            Plan erstellen
                        </Dialog.Title>
                      </div>
                    </div>

                    <div className={'h-auto md:h-[450px] '}>
                      <div className='h-full w-full pt-4 pb-8 '>
                        <div className='relative flex flex-col items-start pl-[0px] justify-between text-white/70 h-full'>
                          Um einen neuen Plan in XPlanung24 anzulegen, laden Sie die XPlan GML-Datei oder einen Ordner mit allen dazugehörigen Dokumenten in den vorgesehenen Bereich hoch.
                          <br/><br/>
                          Sobald der Upload abgeschlossen ist, werden alle Dateien auf der rechten Seite angezeigt, und der Button „Plan erstellen“ wird freigegeben.
                          <br/><br/>
                          Wenn Sie auf „Plan erstellen“ klicken, wird die hochgeladene XPlan GML-Datei verarbeitet und in Ihrem Account gespeichert. Dieser Vorgang dauert nur wenige Sekunden. Danach werden Sie automatisch zum Plan weitergeleitet, um ihn anzusehen.

                        </div>
                      </div>
                    </div>
                  </div>
                  <div className={'md:w-3/5 flex bg-white flex-col w-full'}>
                    <div className='px-8 pt-8 flex flex-col flex-1 overflow-hidden'>

                      {companies.length > 0 && companyScope === '' && (
                          <div className="bg-white mt-6">
                            <ul className="list-disc flex flex-col list-inside gap-2">
                                <SelectInput
                                    label={'Gemeinde'}
                                    id={'company'}
                                    requested={true}
                                    options={companies}
                                    value={companyScope}
                                    setValue={setCompanyScope}
                                />
                            </ul>
                          </div>
                      )}


                      {companyScope !== '' && (
                        <>
                          <PlanFileUploader loading={loading} setLoading={setLoading} uploadedFiles={uploadedFiles} onFilesUploaded={onFilesUploaded} planPath={`${companyScope}/plans/${planId}`}/>

                          <div className="mt-6 overflow-y-auto pr-2">
                            {uploadedFiles.length > 0 && (
                                  <ul className="list-disc flex flex-col list-inside gap-2 my-1">
                                    {uploadedFiles.map((file, index) => (
                                        <li key={`gml-${index}`} className="flex items-center justify-between rounded-lg  text-sm leading-6 bg-gray-200 py-2 px-2">
                                          <FileIcon fileExtension={file.name.split('.').pop()} />
                                          <div className='flex gap-2 w-full truncate ml-2'>
                                            <div className='truncate flex-1'>
                                              <h3 className=' truncate'>{file.name}</h3>
                                              <div className='flex gap-4'>
                                                <p className='text-gray-600/60 text-xs '>{file.type} Datei</p>
                                                <p className='text-gray-600/60 text-xs'>{getFormatedFileSize(+file.size)}</p>
                                              </div>
                                            </div>
                                          </div>
                                          <button onClick={() => handleDeleteFile(file.key)}
                                                  className="cursor-pointer mr-2 ml-4 flex"
                                          >
                                            <FiTrash  className="h-[18px] w-[18px] hover:text-red flex-none text-gray-900"  aria-hidden="true"/>
                                          </button>
                                        </li>
                                    ))}
                                  </ul>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                    <div className="p-8 sm:flex-row-reverse flex w-full flex-col-reverse gap-4">
                      <button
                          type="button"
                          disabled={loading || !validFiles}
                          className={`${loading ? 'cursor-progress' : ''} text-center truncate items-center rounded-md bg-white border-gray-900 border-2 disabled:text-gray-600/40 disabled:border-gray-500 px-4 py-3 text-sm font-semibold text-gray-900 `}
                          onClick={onPlanFinish}
                      >
                        Plan erstellen
                      </button>
                    </div>
                  </div>
                </div>
              </Dialog.Panel>

            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
